import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface PDFOptions {
  filename?: string;
  quality?: number;
  margin?: number;
  useSmartPageBreaks?: boolean;
}

/**
 * Generates a PDF using the native browser print dialog (like learning plans)
 * @param elementId - The ID of the HTML element to convert to PDF
 * @param options - Configuration options for PDF generation
 * @returns Promise that resolves when PDF is generated
 */
export async function generatePDF(
  elementId: string, 
  options: PDFOptions = {}
): Promise<void> {
  const {
    filename = 'document.pdf',
    quality = 0.95,
    margin = 20, // 2cm = 20mm
    useSmartPageBreaks = false // Use native browser printing
  } = options;

  try {
    // Get the HTML element
    const element = document.getElementById(elementId);
    
    if (!element) {
      throw new Error(`Element with id "${elementId}" not found`);
    }

    // Show loading indicator
    const loadingToast = (window as any).showLoadingToast?.('PDF wird vorbereitet...') || null;

    // Use native browser printing (like learning plans)
    await generatePDFNativePrint(element, { filename, quality, margin });

    // Hide loading indicator
    if (loadingToast) {
      loadingToast.dismiss?.();
    }

    // Show success message
    if ((window as any).toast?.success) {
      (window as any).toast.success('PDF-Dialog geöffnet!');
    }

  } catch (error) {
    console.error('Error generating PDF:', error);
    
    // Show error message
    if ((window as any).toast?.error) {
      (window as any).toast.error('Fehler beim Öffnen des PDF-Dialogs');
    }
    
    throw error;
  }
}

/**
 * Native browser print dialog (like learning plans approach)
 */
async function generatePDFNativePrint(
  element: HTMLElement,
  options: { filename: string; quality: number; margin: number }
): Promise<void> {
  const { filename } = options;

  // Create a new window with the print content
  const printWindow = window.open('', '_blank', 'width=800,height=600,menubar=yes,toolbar=yes,scrollbars=yes,resizable=yes');
  
  if (!printWindow) {
    throw new Error('Popup-Blocker verhindert das Öffnen des Druckfensters');
  }

  // Get the element content
  const elementHTML = element.outerHTML;
  
  // Create complete HTML document with proper print styles
  const htmlContent = `
<!DOCTYPE html>
<html lang="de">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Curriculum: ${filename}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
            line-height: 1.6;
            color: #333;
            background: white;
            padding: 0;
            margin: 0;
        }
        
        .print-container {
            max-width: none;
            margin: 0;
            padding: 0;
            background: white;
        }
        
        /* Print-specific styles */
        @media print {
            @page {
                margin: 2cm;
                size: A4 portrait;
            }
            
            * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
            }
            
            body {
                background: white !important;
                padding: 0 !important;
                margin: 0 !important;
                font-size: 11pt !important;
                line-height: 1.5 !important;
            }
            
            .print-container {
                width: 100% !important;
                max-width: none !important;
                margin: 0 !important;
                padding: 0 !important;
            }
            
            /* Prevent page breaks inside elements */
            .competency-item,
            .content-field-item,
            .mapping-item {
                page-break-inside: avoid;
                break-inside: avoid;
            }
            
            /* Section headings should not break from content */
            .section-heading {
                page-break-after: avoid;
                break-after: avoid;
                orphans: 4;
            }
            
            /* Keep section content together when possible */
            .section-content {
                page-break-inside: avoid;
                break-inside: auto;
            }
            
            /* PDF Header and Footer */
            .pdf-header {
                page-break-after: avoid;
                break-after: avoid;
            }
            
            .pdf-footer {
                page-break-before: avoid;
                break-before: avoid;
            }
            
            /* Ensure colors and gradients are printed */
            .bg-gray-50,
            .bg-gray-100 {
                background-color: #f9fafb !important;
            }
            
            /* Ensure links are visible */
            a {
                text-decoration: underline;
            }
            
            /* Better page break control */
            p, div {
                orphans: 3;
                widows: 3;
            }
            
            /* Ensure logo images are printed with correct size */
            img {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                max-width: 100%;
                height: auto;
            }
            
            /* Specific logo sizing */
            .pdf-header img,
            .pdf-footer img {
                width: 5cm !important;
                height: 5cm !important;
                object-fit: contain;
            }
        }
        
        /* Screen styles */
        .print-button {
            position: fixed;
            bottom: 30px;
            right: 30px;
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            color: white;
            border: none;
            padding: 15px 25px;
            border-radius: 50px;
            font-weight: 600;
            cursor: pointer;
            box-shadow: 0 10px 25px rgba(37, 99, 235, 0.3);
            transition: all 0.3s ease;
            z-index: 1000;
            font-size: 16px;
            min-width: 120px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
        }
        
        .print-button:hover {
            transform: translateY(-3px);
            box-shadow: 0 15px 35px rgba(37, 99, 235, 0.4);
            background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
        }
        
        .print-button:focus {
            outline: 2px solid #2563eb;
            outline-offset: 2px;
        }
        
        @media print {
            .print-button {
                display: none !important;
            }
        }
    </style>
</head>
<body>
    ${elementHTML}
    
    <button class="print-button" onclick="printDocument()" title="Curriculum drucken - Öffnet das Betriebssystem-Druckerpanel (Strg+P)" type="button">
        <span>🖨️</span>
        <span>Drucken</span>
    </button>
    
    <script>
        let printInProgress = false;
        
        function printDocument() {
            if (printInProgress) {
                console.log('⚠️ Druckvorgang bereits aktiv');
                return;
            }
            
            printInProgress = true;
            console.log('🖨️ DRUCKEN-BUTTON GEKLICKT - Starte Druckvorgang...');
            
            try {
                if (window.print && typeof window.print === 'function') {
                    console.log('✅ window.print() verfügbar - rufe Druckerdialog auf...');
                    window.print();
                    console.log('✅ window.print() wurde aufgerufen');
                    
                    setTimeout(() => {
                        printInProgress = false;
                        console.log('🔄 Print-Status zurückgesetzt');
                    }, 1000);
                    
                    return;
                }
                
                console.log('❌ window.print() nicht verfügbar');
                alert('Das Druckerpanel konnte nicht automatisch geöffnet werden.\\n\\n' +
                      'Bitte drücken Sie:\\n' +
                      '• Windows/Linux: Strg + P\\n' +
                      '• Mac: Cmd + P\\n' +
                      '• Oder Rechtsklick → "Drucken"');
                
            } catch (error) {
                console.error('❌ Fehler beim Drucken:', error);
                alert('Unerwarteter Fehler beim Drucken.\\n\\n' +
                      'Bitte verwenden Sie:\\n' +
                      '• Strg+P (Windows/Linux)\\n' +
                      '• Cmd+P (Mac)');
            } finally {
                setTimeout(() => {
                    printInProgress = false;
                    console.log('🔄 Print-Status final zurückgesetzt');
                }, 3000);
            }
        }
        
        // Auto-focus for better UX
        window.addEventListener('load', function() {
            console.log('📄 Curriculum-Druckfenster geladen');
        });
    </script>
</body>
</html>`;

  // Write content to new window
  printWindow.document.open();
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  
  // Focus the new window
  printWindow.focus();
  
  // Optional: Auto-print after a short delay
  setTimeout(() => {
    if (printWindow.print && typeof printWindow.print === 'function') {
      printWindow.print();
    }
  }, 500);
}

/**
 * Simple PDF generation without smart breaks
 */
async function generatePDFSimple(
  element: HTMLElement,
  options: { filename: string; quality: number; margin: number }
): Promise<void> {
  const { filename, quality, margin } = options;

  // Create a clean copy of the element for rendering
  const clonedElement = element.cloneNode(true) as HTMLElement;
  
  // Apply print styles to cloned element
  clonedElement.style.position = 'absolute';
  clonedElement.style.top = '-9999px';
  clonedElement.style.left = '-9999px';
  clonedElement.style.width = '800px';
  clonedElement.style.backgroundColor = '#ffffff';
  clonedElement.style.padding = '20px';
  clonedElement.style.fontFamily = 'Arial, sans-serif';
  
  // Add to DOM temporarily
  document.body.appendChild(clonedElement);
  
  try {
    const canvas = await html2canvas(clonedElement, {
      scale: 1.5,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: 800,
      height: clonedElement.scrollHeight,
      allowTaint: true,
      foreignObjectRendering: false,
      removeContainer: false
    });

    // Remove cloned element
    document.body.removeChild(clonedElement);

    const pageWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const contentWidth = pageWidth - (margin * 2);
    const contentHeight = pageHeight - (margin * 2);
    
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const totalPages = Math.ceil(imgHeight / contentHeight);
    
    // Add pages with proper margins and page numbers
    for (let page = 0; page < totalPages; page++) {
      if (page > 0) {
        pdf.addPage();
      }
      
      const yPosition = margin - (page * contentHeight);
      const imgData = canvas.toDataURL('image/jpeg', quality);
      
      pdf.addImage(imgData, 'JPEG', margin, yPosition, imgWidth, imgHeight);
      
      // Add page number in footer with proper positioning
      const pageNumberY = pageHeight - (margin / 2);
      pdf.setFontSize(9);
      pdf.setTextColor(150);
      pdf.text(
        `Seite ${page + 1} von ${totalPages}`, 
        pageWidth / 2, 
        pageNumberY, 
        { align: 'center' }
      );
    }

    pdf.save(filename);
    
  } catch (error) {
    // Clean up cloned element if error occurs
    if (document.body.contains(clonedElement)) {
      document.body.removeChild(clonedElement);
    }
    throw error;
  }
}

/**
 * Smart PDF generation that respects element boundaries
 */
async function generatePDFWithSmartBreaks(
  element: HTMLElement,
  options: { filename: string; quality: number; margin: number }
): Promise<void> {
  const { filename, quality, margin } = options;

  const pageWidth = 210;
  const pageHeight = 297;
  const contentWidth = pageWidth - (margin * 2);
  const contentHeight = pageHeight - (margin * 2);
  
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  // Find all major sections that should not be split
  const breakableElements = element.querySelectorAll(
    '.competency-item, .content-field-item, .mapping-item, .section-content, .section-heading, .pdf-header, .pdf-footer'
  );

  // Get element positions and heights
  interface ElementInfo {
    element: Element;
    top: number;
    height: number;
    bottom: number;
  }

  const elementInfos: ElementInfo[] = [];
  const containerTop = element.getBoundingClientRect().top;

  breakableElements.forEach((el) => {
    const rect = el.getBoundingClientRect();
    elementInfos.push({
      element: el,
      top: rect.top - containerTop,
      height: rect.height,
      bottom: rect.bottom - containerTop
    });
  });

  // Render the full element to canvas first
  const fullCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight
  });

  // Calculate pixel to mm ratio
  const pxToMmRatio = contentWidth / fullCanvas.width;
  
  // Split into pages intelligently
  let currentPageTop = 0; // in pixels
  let pageNumber = 1;
  const pages: Array<{ startPx: number; endPx: number }> = [];

  while (currentPageTop < fullCanvas.height) {
    const pageEndTarget = currentPageTop + (contentHeight / pxToMmRatio);
    
    // Find the best break point
    let bestBreakPoint = pageEndTarget;
    
    // Look for elements that would be cut
    for (const info of elementInfos) {
      const elTopPx = info.top * (fullCanvas.height / element.scrollHeight);
      const elBottomPx = info.bottom * (fullCanvas.height / element.scrollHeight);
      
      // If element starts before page end but ends after, it would be cut
      if (elTopPx < pageEndTarget && elBottomPx > pageEndTarget) {
        // If element is small enough to fit on page, break before it
        if (elBottomPx - elTopPx < (contentHeight / pxToMmRatio) * 0.8) {
          // Element fits, move break point before element
          if (elTopPx > currentPageTop + 50) { // At least 50px on page
            bestBreakPoint = elTopPx;
            break;
          } else {
            // Element is at top of page, break after it
            bestBreakPoint = elBottomPx;
            break;
          }
        }
      }
    }
    
    pages.push({
      startPx: currentPageTop,
      endPx: Math.min(bestBreakPoint, fullCanvas.height)
    });
    
    currentPageTop = bestBreakPoint;
    
    // Safety check
    if (pages.length > 50) break;
  }

  // Create PDF pages
  const imgData = fullCanvas.toDataURL('image/jpeg', quality);
  
  for (let i = 0; i < pages.length; i++) {
    if (i > 0) {
      pdf.addPage();
    }
    
    const page = pages[i];
    const pageHeightPx = page.endPx - page.startPx;
    const pageHeightMm = pageHeightPx * pxToMmRatio;
    
    // Draw the slice of the canvas
    const yPosition = margin - (page.startPx * pxToMmRatio);
    const imgHeight = fullCanvas.height * pxToMmRatio;
    
    pdf.addImage(
      imgData,
      'JPEG',
      margin,
      yPosition,
      contentWidth,
      imgHeight
    );
    
    // Add page number
    const pageNumberY = pageHeight - (margin / 2);
    pdf.setFontSize(9);
    pdf.setTextColor(150);
    pdf.text(
      `Seite ${i + 1} von ${pages.length}`, 
      pageWidth / 2, 
      pageNumberY, 
      { align: 'center' }
    );
  }

  pdf.save(filename);
}

/**
 * Generates a PDF from a React component by temporarily rendering it
 * @param element - The React element to render
 * @param options - Configuration options for PDF generation
 * @returns Promise that resolves when PDF is generated
 */
export async function generatePDFFromElement(
  element: HTMLElement,
  options: PDFOptions = {}
): Promise<void> {
  const {
    filename = 'document.pdf',
    quality = 0.95,
    margin = 20, // 2cm = 20mm
    useSmartPageBreaks = false
  } = options;

  try {
    // Use native browser printing (like learning plans)
    await generatePDFNativePrint(element, { filename, quality, margin });
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
}

/**
 * Sanitizes a filename for use in PDF generation
 * @param name - The original filename
 * @returns Sanitized filename
 */
export function sanitizeFilename(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9äöüÄÖÜß_-]/g, '_')
    .replace(/_{2,}/g, '_')
    .substring(0, 100) + '.pdf';
}

